import ExcelJS from 'exceljs';
import { SceneWithUserNames } from '../../../types/scene.types';
import { BreakdownInfo } from '../../../pages/Home/types';
import { ElementType, ElementInfo } from '../../../types/element.types';
import { ElementDataCache, ElementRelationDataCache } from '../../../types/cache.types';
import { compareSceneCodes, SceneCodeParts } from '../../../pages/Breakdown/BreakdownMain/Utils/SceneCodeUtils';
import { sortCastItemsByNO, compareExportNamesDigitLetterPinyin } from '../../../utils/elementSortUtils';
import i18n from '../../../i18n';
import { formatDayNightForExport, formatInteriorExteriorForExport } from '../exportSceneFieldFormat';
import { buildExportDownloadFileName } from '../exportFileName';
import { writeWorkbookBufferWithProgress } from '../excelWriteProgress';
// 语言类型定义
export type SupportedLanguage = 'zh-CN' | 'zh-HK' | 'en';

const t = (key: string, opts?: Record<string, unknown> & { lng?: string }) =>
  i18n.t(key, opts);

// 颜色常量（十六进制格式；与 app/src/theme/color 中 CSS 变量一致）
const COLORS = {
  GRAY3: '#757575', // --color-gray3（表头背景，不变）
  BLACK: '#000000', // 正文与边框：纯黑，便于纸质打印
  BORDER: '#000000'
};

// 冻结第 1、2 行与第 1 列；可滚动区域从 B3 起
const SHEET_FREEZE_VIEWS: ExcelJS.WorksheetView[] = [
  {
    state: 'frozen',
    xSplit: 1,
    ySplit: 2,
    topLeftCell: 'B3',
    activeCell: 'B3'
  } as ExcelJS.WorksheetView
];

export const EXCEL_SCENES_AND_SETS_MARGINS_INCH = {
  left: 0.35,
  right: 0.35,
  top: 0.35,
  bottom: 0.5,
  header: 0.3,
  footer: 0.3
} as const;

/** 默认打印布局（不改变工作表视图为「页面布局」模式，仅写入页面设置） */
function applyScenesAndSetsPrintLayout(worksheet: ExcelJS.Worksheet, language: SupportedLanguage) {
  worksheet.pageSetup = {
    ...worksheet.pageSetup,
    orientation: 'landscape',
    horizontalCentered: true,
    margins: { ...EXCEL_SCENES_AND_SETS_MARGINS_INCH },
    printTitlesRow: '1:2',
    // 打印时每页左侧重复第 1 列（场号列），与冻结窗格 xSplit: 1 一致
    printTitlesColumn: 'A:A'
  };
  const footerBody = t('export.excelPageFooter', { lng: language });
  worksheet.headerFooter = {
    ...worksheet.headerFooter,
    // 中段：&C 居中、&8 字号 8pt；文案含 Excel 页码码 &P/&N（见各语言 excelPageFooter）
    oddFooter: `&C&8${footerBody}`
  };
}

// 将十六进制颜色转换为 ExcelJS ARGB 格式（'FF' + 6位十六进制）
const hexToArgb = (hex: string): string => {
  // 移除 # 号（如果有）
  const cleanHex = hex.replace('#', '');
  // 返回 ARGB 格式：'FF' + 6位十六进制颜色代码
  return `FF${cleanHex.toUpperCase()}`;
};

// 设置单元格边框颜色
const setCellBorder = (cell: ExcelJS.Cell) => {
  const borderColor = { argb: hexToArgb(COLORS.BORDER) };
  cell.border = {
    top: { style: 'thin', color: borderColor },
    left: { style: 'thin', color: borderColor },
    bottom: { style: 'thin', color: borderColor },
    right: { style: 'thin', color: borderColor }
  };
};

// 字段映射 - 从 i18n 根据语言获取
const getFieldMapForLanguage = (language: SupportedLanguage) => ({
  sceneCode: t('export.sceneCode', { lng: language }),
  interiorExterior: t('export.interiorExterior', { lng: language }),
  dayNight: t('export.dayNight', { lng: language }),
  sceneSet: t('export.sceneSet', { lng: language }),
  scenePlotDay: t('export.scenePlotDay', { lng: language }),
  scenePagesNumber: t('export.scenePagesNumber', { lng: language }),
  scenePlot: t('export.scenePlot', { lng: language }),
  sceneLocation: t('export.sceneLocation', { lng: language }),
  scenePeriod: t('export.scenePeriod', { lng: language }),
  cast: t('export.cast', { lng: language }),
  group1: t('export.group1', { lng: language }),
  group2: t('export.group2', { lng: language }),
  // 分组3/4 与列顺序一致：group3=服装等（原 export.group4），group4=美术道具（原 export.group3）
  group3: t('export.group4', { lng: language }),
  group4: t('export.group3', { lng: language }),
  group5: t('export.group5', { lng: language }),
  group6: t('export.group6', { lng: language }),
  group7: t('export.group7', { lng: language }),
  group8: t('export.group8', { lng: language })
});

// 将场次元素按分组规则分类，返回分组文本数组
const groupElements = (elements: any[], language: SupportedLanguage): string[] => {
  const lng = language;
  const itemTypeNameMap: Record<ElementType, string> = {
    [ElementType.FEATURED]: t('elementType.FEATURED', { lng }),
    [ElementType.BACKGROUND]: t('elementType.BACKGROUND', { lng }),
    [ElementType.STUNT_PLAYERS]: t('elementType.STUNT_PLAYERS', { lng }),
    [ElementType.ANIMALS]: t('elementType.ANIMALS', { lng }),
    [ElementType.VEHICLES]: t('elementType.VEHICLES', { lng }),
    [ElementType.ART_PROPS]: t('elementType.ART_PROPS', { lng }),
    [ElementType.WARDROBE]: t('elementType.WARDROBE', { lng }),
    [ElementType.HAIR_MAKEUP]: t('elementType.HAIR_MAKEUP', { lng }),
    [ElementType.SFX_MU]: t('elementType.SFX_MU', { lng }),
    [ElementType.SFX]: t('elementType.SFX', { lng }),
    [ElementType.VFX]: t('elementType.VFX', { lng }),
    [ElementType.CAMERA]: t('elementType.CAMERA', { lng }),
    [ElementType.LIGHTING]: t('elementType.LIGHTING', { lng }),
    [ElementType.SOUND]: t('elementType.SOUND', { lng }),
    [ElementType.ADDL_LABOR]: t('elementType.ADDL_LABOR', { lng }),
    [ElementType.SPECIAL_EQUIPMENT]: t('elementType.SPECIAL_EQUIPMENT', { lng }),
    [ElementType.MISC]: t('elementType.MISC', { lng }),
    [ElementType.NOTE]: t('elementType.NOTE', { lng }),
    [ElementType.CAST]: t('elementType.CAST', { lng })
  };

  // 定义分组映射
  const groupMap: Record<ElementType, number> = {
    [ElementType.CAST]: 0, // 主要角色不参与分组
    [ElementType.FEATURED]: 1,
    [ElementType.BACKGROUND]: 1,
    [ElementType.STUNT_PLAYERS]: 1,
    [ElementType.ANIMALS]: 2,
    [ElementType.VEHICLES]: 2,
    [ElementType.ART_PROPS]: 3,
    [ElementType.WARDROBE]: 4,
    [ElementType.HAIR_MAKEUP]: 4,
    [ElementType.SFX_MU]: 4,
    [ElementType.SFX]: 5,
    [ElementType.VFX]: 6,
    [ElementType.CAMERA]: 7,
    [ElementType.LIGHTING]: 7,
    [ElementType.SOUND]: 7,
    [ElementType.ADDL_LABOR]: 7,
    [ElementType.SPECIAL_EQUIPMENT]: 7,
    [ElementType.MISC]: 7,
    [ElementType.NOTE]: 8
  };

  // 按分组和类型分类元素
  const groups = new Map<number, Map<ElementType, any[]>>();
  
  elements.forEach(item => {
    const elementType = item.elementType as ElementType;
    const groupId = groupMap[elementType];
    if (groupId > 0) {
      if (!groups.has(groupId)) {
        groups.set(groupId, new Map<ElementType, any[]>());
      }
      const group = groups.get(groupId)!;
      if (!group.has(elementType)) {
        group.set(elementType, []);
      }
      group.get(elementType)?.push(item);
    }
  });

  // 生成格式化的分组文本数组（8个分组）
  const formattedGroups: string[] = new Array(8).fill('');
  
  // 定义每个分组内的类型排序顺序
  const groupTypeOrder: Record<number, ElementType[]> = {
    1: [
      ElementType.FEATURED,        // 特约角色
      ElementType.BACKGROUND, // 群众演员
      ElementType.STUNT_PLAYERS           // 动作演员
    ],
    2: [
      ElementType.ANIMALS,               // 动物
      ElementType.VEHICLES               // 车辆
    ],
    4: [
      ElementType.WARDROBE,              // 服装
      ElementType.HAIR_MAKEUP,               // 梳头化妆
      ElementType.SFX_MU            // 特效化妆
    ],
    7: [
      ElementType.CAMERA,               // 摄影
      ElementType.LIGHTING,              // 灯光
      ElementType.SOUND,                // 收音
      ElementType.ADDL_LABOR,       // 外增人员
      ElementType.SPECIAL_EQUIPMENT,     // 特殊器材
      ElementType.MISC                 // 其他
    ]
  };

  // 按分组ID顺序处理
  for (let i = 1; i <= 8; i++) {
    if (groups.has(i)) {
      const group = groups.get(i)!;
      if (group.size > 0) {
        let groupText = '';
        
        // 按类型顺序处理分组内的元素
        // 如果分组有定义的排序顺序，使用定义的顺序；否则使用字典序排序
        let itemTypes: ElementType[];
        if (groupTypeOrder[i]) {
          // 使用定义的顺序，但只包含实际存在的类型
          itemTypes = groupTypeOrder[i].filter(type => group.has(type));
        } else {
          // 其他分组使用字典序排序
          itemTypes = Array.from(group.keys()).sort();
        }
        
        itemTypes.forEach((itemType, index) => {
          const items = group.get(itemType)!;
          if (items.length > 0) {
            const typeName = itemTypeNameMap[itemType];
            // 美术道具、现场特效、电脑特效、备注列不打印分类标题及「：」
            const omitTypeLabel = i === 3 || i === 5 || i === 6 || i === 8;
            
            // 对元素按名称排序：数字、英文、拼音（与 elementSortUtils 导出规则一致）
            const sortedItems = [...items].sort((a, b) => {
              const nameA = (a.elementName || a.elementAbbr || '').trim();
              const nameB = (b.elementName || b.elementAbbr || '').trim();
              return compareExportNamesDigitLetterPinyin(nameA, nameB);
            });

            // 格式化每个元素
            const itemTexts = sortedItems.map(item => {
              const quantity = item.elementQtyInScene || 1;
              const elementName = (item.elementName || item.elementAbbr || '').trim();
              // 只有分组1和分组2需要显示数量
              if (i === 1 || i === 2) {
                return `${elementName}*${quantity}`;
              } else {
                return elementName;
              }
            });
            
            const joined = itemTexts.join(' / ');
            const listColon = t('export.elementTypeListColon', { lng: language });
            let typeText = omitTypeLabel ? joined : `${typeName}${listColon}${joined}`;
            
            // 每个分类用★隔开，不断行，显示为单行文本
            if (index < itemTypes.length - 1) {
              typeText += '★';
            }
            
            groupText += typeText;
          }
        });
        
        if (groupText) {
          formattedGroups[i - 1] = groupText; // 分组索引从1开始，数组索引从0开始
        }
      }
    }
  }
  
  return formattedGroups;
};

// 获取主要角色列文案（从缓存）
const getCastColumnTextForScene = (
  sceneId: string,
  elementsBySceneId: Map<string, ElementInfo[]>
): string => {
  const elements = elementsBySceneId.get(sceneId) || [];
  
  if (!Array.isArray(elements) || elements.length === 0) {
    return '';
  }
  
  const castItems = elements.filter(
    (item: ElementInfo) => item.elementType === ElementType.CAST
  );
  
  const sortedCastItems = sortCastItemsByNO(castItems);
  
  const roleNames = sortedCastItems
    .map((item: ElementInfo) => item.elementAbbr || item.elementName || '')
    .filter(Boolean);
  
  return roleNames.join('/') || '';
};

// 获取场次元素数据的辅助函数（从缓存获取）
const getElementsForScene = (
  sceneId: string,
  language: SupportedLanguage,
  elementsBySceneId: Map<string, ElementInfo[]>
): string[] => {
  const elements = elementsBySceneId.get(sceneId) || [];
  
  if (!Array.isArray(elements) || elements.length === 0) {
    return new Array(8).fill('');
  }
  
  // 按规则对元素分类进行分组
  return groupElements(elements, language);
};

// 检查场景的所有字段是否都为空
const isSceneRowEmpty = (scene: SceneWithUserNames): boolean => {
  // 检查所有导出字段是否都为空
  const hasSceneCode = !!scene.sceneCode?.trim();
  const hasInteriorExterior = !!scene.sceneInteriorExterior;
  const hasDayNight = !!scene.sceneDayNight;
  const hasSceneSet = !!scene.sceneSet?.trim();
  const hasScenePlotDay = !!scene.scenePlotDay?.trim();
  const hasScenePagesNumber = !!scene.scenePagesNumber;
  const hasScenePlot = !!scene.scenePlot?.trim();
  const hasSceneLocation = !!scene.sceneLocation?.trim();
  const hasScenePeriod = !!scene.scenePeriod?.trim();
  
  // 如果所有字段都为空，则返回true（表示该行应该被过滤）
  return !hasSceneCode && 
         !hasInteriorExterior && 
         !hasDayNight && 
         !hasSceneSet && 
         !hasScenePlotDay && 
         !hasScenePagesNumber && 
         !hasScenePlot && 
         !hasSceneLocation && 
         !hasScenePeriod;
};

// 按场号排序
const sortBySceneCode = (scenes: SceneWithUserNames[], parseCache: Map<string, SceneCodeParts | null>): SceneWithUserNames[] => {
  return [...scenes]
    .filter(scene => !isSceneRowEmpty(scene)) // 过滤所有字段都为空的行
    .sort((a, b) => {
      const codeCompare = compareSceneCodes(a, b, parseCache);
      if (codeCompare === 0) {
        return (a.scenePlotDay || '').localeCompare(b.scenePlotDay || '');
      }
      return codeCompare;
    });
};

// 按拍摄地点排序
const sortBySceneLocation = (scenes: SceneWithUserNames[], parseCache: Map<string, SceneCodeParts | null>): SceneWithUserNames[] => {
  return [...scenes]
    .filter(scene => !isSceneRowEmpty(scene)) // 过滤所有字段都为空的行
    .sort((a, b) => {
      const locationA = (a.sceneLocation || '').trim();
      const locationB = (b.sceneLocation || '').trim();
      
      // 如果两个都是空，按场号排序
      if (!locationA && !locationB) {
        return compareSceneCodes(a, b, parseCache);
      }
      // 如果只有一个是空，空的排在后面
      if (!locationA) return 1;
      if (!locationB) return -1;
      
      // 两个都不为空，按拍摄地点排序
      const locationCompare = locationA.localeCompare(locationB, 'zh-CN', { numeric: true, sensitivity: 'base' });
      // 如果拍摄地点相同，按场号排序
      if (locationCompare === 0) {
        return compareSceneCodes(a, b, parseCache);
      }
      return locationCompare;
    });
};

// 格式化日期时间
const formatDate = (date: Date | string, displayLanguage: SupportedLanguage) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

// 导出Excel
export const exportScenesAndSetsExcel = async (
  scenes: SceneWithUserNames[],
  breakdownInfo: BreakdownInfo,
  language: SupportedLanguage = 'zh-CN',
  secondaryLanguage?: SupportedLanguage,
  breakdownId?: string,
  getElementCache?: (breakdownId: string) => ElementDataCache | null,
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null,
  onProgress?: (progress: number) => void
): Promise<void> => {
  onProgress?.(10); // 开始导出

  const fieldMap = getFieldMapForLanguage(language);
  
  // 创建场号解析缓存
  const parseCache = new Map<string, SceneCodeParts | null>();
  
  // 排序：分场表按场号排序，分景表按拍摄地点排序
  const scenesSortedByCode = sortBySceneCode(scenes, parseCache);
  const scenesSortedByLocation = sortBySceneLocation(scenes, parseCache);
  
  onProgress?.(20); // 排序完成

  // 从缓存获取 elements 和 elementRelations
  const elementsBySceneId = new Map<string, ElementInfo[]>();
  
  if (breakdownId && getElementCache && getElementRelationCache) {
    const elementCache = getElementCache(breakdownId);
    const elementRelationCache = getElementRelationCache(breakdownId);
    
    const allElements = elementCache?.elements || [];
    const allRelations = elementRelationCache?.elementRelations || [];
    
    // 创建 elementID 到 ElementInfo 的映射
    const elementMap = new Map<string, ElementInfo>();
    allElements.forEach(item => {
      elementMap.set(item.elementID, item);
    });
    
    // 根据关系，将 elements 按 sceneID 分组，并合并关系中的数量信息
    allRelations.forEach(relation => {
      const element = elementMap.get(relation.elementID);
      if (element) {
        // 创建一个副本，并添加关系中的数量信息
        const enrichedItem = {
          ...element,
          elementQtyInScene: relation.elementQtyInScene || 1
        };
        const existing = elementsBySceneId.get(relation.sceneID) || [];
        elementsBySceneId.set(relation.sceneID, [...existing, enrichedItem]);
      }
    });
  }

  // 批量获取所有场次的数据（从缓存）
  const castTextBySceneId = new Map<string, string>();
  const elementsMap = new Map<string, string[]>();
  
  const allSceneIds = Array.from(new Set([...scenesSortedByCode, ...scenesSortedByLocation].map(s => s.sceneID)));
  
  allSceneIds.forEach((sceneId) => {
    const castColumnText = getCastColumnTextForScene(sceneId, elementsBySceneId);
    castTextBySceneId.set(sceneId, castColumnText);
    
    const elements = getElementsForScene(sceneId, language, elementsBySceneId);
    elementsMap.set(sceneId, elements);
  });
  
  onProgress?.(50); // 数据获取完成

  // 构建表头（排期在主要角色列右方；分组3=服装/梳头/化妆/特效化妆，分组4=美术道具）
  const headers = [
    fieldMap.sceneCode,
    fieldMap.interiorExterior,
    fieldMap.dayNight,
    fieldMap.sceneSet,
    fieldMap.scenePlotDay,
    fieldMap.scenePagesNumber,
    fieldMap.scenePlot,
    fieldMap.sceneLocation,
    fieldMap.cast,
    fieldMap.scenePeriod,
    fieldMap.group1,
    fieldMap.group2,
    fieldMap.group3,
    fieldMap.group4,
    fieldMap.group5,
    fieldMap.group6,
    fieldMap.group7,
    fieldMap.group8
  ];

  // 获取影片名称
  const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') : null;
  
  // 准备标题和文件信息文本
  const versionText = t('export.version', { lng: language });
  const scriptVersionText = t('export.scriptVersion', { lng: language });
  const lastUpdatedText = t('export.lastUpdate', { lng: language });

  const updateTimeText = formatDate(breakdownInfo.breakdownUpdateTime || new Date(), language);
  const filmNameTrimmed = (filmName || '').trim();
  const filmPrefix = filmNameTrimmed
    ? (t('export.excelSheetFirstRowFilmPrefix', {
        lng: language,
        filmName: filmNameTrimmed
      }) as string)
    : '';

  const buildSheetFirstRowTitle = (tableLabel: string) =>
    t('export.excelSheetFirstRowTitle', {
      lng: language,
      filmPrefix,
      tableLabel,
      versionLabel: versionText,
      breakdownName: breakdownInfo.breakdownName || '',
      scriptVersionLabel: scriptVersionText,
      scriptOverview: breakdownInfo.breakdownScriptOverview || '',
      lastUpdatedLabel: lastUpdatedText,
      updateTime: updateTimeText
    }) as string;

  const scenesTableLabel = t('export.excelBreakdownBannerLabel', { lng: language });
  const scenesSheetTitle = buildSheetFirstRowTitle(scenesTableLabel);

  const setsTableLabel = t('export.excelSetsBannerLabel', { lng: language });
  const setsTitle = buildSheetFirstRowTitle(setsTableLabel);

  // 创建工作簿
  const workbook = new ExcelJS.Workbook();

  // 创建场次行工作表
  const scenesWorksheet = workbook.addWorksheet(t('export.excelBreakdownBannerLabel', { lng: language }));

  // 设置列宽（参考PDF模板的列宽比例）
  scenesWorksheet.columns = [
    { width: 10 }, // 场号
    { width: 4 },  // 内外
    { width: 4 },  // 日夜
    { width: 20 }, // 场景
    { width: 8 },  // 剧情日
    { width: 4 },  // 页数
    { width: 30 }, // 剧情
    { width: 25 }, // 拍摄地点
    { width: 25 }, // 主要角色
    { width: 8 },  // 排期
    { width: 50 }, // 分组1
    { width: 10 }, // 分组2
    { width: 70 }, // 分组3（服裝/梳頭/化妝/特效化妝）
    { width: 130 }, // 分组4（美術道具）
    { width: 30 }, // 分组5
    { width: 30 }, // 分组6
    { width: 30 }, // 分组7
    { width: 40 }  // 分组8
  ];

  // 添加标题行（所有信息放在第一个单元格）
  const titleRow = scenesWorksheet.addRow([]);
  titleRow.getCell(1).value = scenesSheetTitle;
  
  // 设置标题样式（8号字体，不加粗，不换行，允许溢出）
  const titleCell = scenesWorksheet.getCell(1, 1);
  titleCell.font = { size: 8, bold: false, color: { argb: hexToArgb(COLORS.BLACK) } };
  titleCell.alignment = { 
    vertical: 'middle', 
    horizontal: 'left',
    wrapText: false // 不换行，允许溢出
  };

  // 添加表头（第2行）
  const headerRow = scenesWorksheet.addRow(headers);
  
  // 设置表头样式（--color-gray3 背景，黑色文字，不加粗，8号字体）
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: hexToArgb(COLORS.GRAY3) }
    };
    cell.font = {
      color: { argb: hexToArgb(COLORS.BLACK) },
      bold: false,
      size: 8
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'left'
    };
    // 设置边框颜色
    setCellBorder(cell);
  });

  // 添加数据行
  scenesSortedByCode.forEach(scene => {
    const elements = elementsMap.get(scene.sceneID) || new Array(8).fill('');
    const row = scenesWorksheet.addRow([
      scene.sceneCode || '',
      formatInteriorExteriorForExport(language, scene.sceneInteriorExterior, secondaryLanguage),
      formatDayNightForExport(language, scene.sceneDayNight, secondaryLanguage),
      scene.sceneSet || '',
      scene.scenePlotDay || '',
      scene.scenePagesNumber || '',
      scene.scenePlot || '',
      scene.sceneLocation || '',
      castTextBySceneId.get(scene.sceneID) || '',
      scene.scenePeriod || '',
      elements[0] || '', // 分组1
      elements[1] || '', // 分组2
      elements[3] || '', // 分组3（服裝/梳頭/化妝/特效化妝）
      elements[2] || '', // 分组4（美術道具）
      elements[4] || '', // 分组5
      elements[5] || '', // 分组6
      elements[6] || '', // 分组7
      elements[7] || ''  // 分组8
    ]);
    
    // 设置数据行样式（8号字体，不加粗，垂直居中，左对齐）
    row.eachCell((cell) => {
      cell.font = { size: 8, bold: false, color: { argb: hexToArgb(COLORS.BLACK) } };
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'left',
        wrapText: false // 关闭自动换行，允许内容溢出到右侧空单元格
      };
      // 设置边框颜色
      setCellBorder(cell);
    });
  });

  scenesWorksheet.views = SHEET_FREEZE_VIEWS;
  applyScenesAndSetsPrintLayout(scenesWorksheet, language);

  // 创建分景表
  const setsWorksheet = workbook.addWorksheet(t('export.excelSetsBannerLabel', { lng: language }));

  // 设置列宽（拍摄地点在剧情与主要角色之间；分组3=服装…，分组4=美术道具）
  setsWorksheet.columns = [
    { width: 10 }, // 场号
    { width: 4 },  // 内外
    { width: 4 },  // 日夜
    { width: 20 }, // 场景
    { width: 8 },  // 剧情日
    { width: 4 },  // 页数
    { width: 30 }, // 剧情
    { width: 25 }, // 拍摄地点
    { width: 25 }, // 主要角色
    { width: 8 },  // 排期
    { width: 50 }, // 分组1
    { width: 10 }, // 分组2
    { width: 70 }, // 分组3（服裝/梳頭/化妝/特效化妝）
    { width: 130 }, // 分组4（美術道具）
    { width: 30 }, // 分组5
    { width: 30 }, // 分组6
    { width: 30 }, // 分组7
    { width: 40 }  // 分组8
  ];

  // 添加标题行（所有信息放在第一个单元格）
  const setsTitleRow = setsWorksheet.addRow([]);
  setsTitleRow.getCell(1).value = setsTitle;
  
  // 设置标题样式（8号字体，不加粗，不换行，允许溢出）
  const setsTitleCell = setsWorksheet.getCell(1, 1);
  setsTitleCell.font = { size: 8, bold: false, color: { argb: hexToArgb(COLORS.BLACK) } };
  setsTitleCell.alignment = { 
    vertical: 'middle', 
    horizontal: 'left',
    wrapText: false // 不换行，允许溢出
  };

  // 构建分景表表头（拍摄地点在剧情与主要角色之间；分组3=服装…，分组4=美术道具）
  const setsHeaders = [
    fieldMap.sceneCode,     // 场号
    fieldMap.interiorExterior,
    fieldMap.dayNight,
    fieldMap.sceneSet,
    fieldMap.scenePlotDay,
    fieldMap.scenePagesNumber,
    fieldMap.scenePlot,
    fieldMap.sceneLocation,  // 拍摄地点
    fieldMap.cast,
    fieldMap.scenePeriod,
    fieldMap.group1,
    fieldMap.group2,
    fieldMap.group3,
    fieldMap.group4,
    fieldMap.group5,
    fieldMap.group6,
    fieldMap.group7,
    fieldMap.group8
  ];
  
  // 添加表头（第2行）
  const setsHeaderRow = setsWorksheet.addRow(setsHeaders);
  
  // 设置表头样式（--color-gray3 背景，黑色文字，不加粗，8号字体）
  setsHeaderRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: hexToArgb(COLORS.GRAY3) }
    };
    cell.font = {
      color: { argb: hexToArgb(COLORS.BLACK) },
      bold: false,
      size: 8
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'left'
    };
    // 设置边框颜色
    setCellBorder(cell);
  });

  // 添加数据行（拍摄地点在剧情与主要角色之间）
  scenesSortedByLocation.forEach(scene => {
    const elements = elementsMap.get(scene.sceneID) || new Array(8).fill('');
    const row = setsWorksheet.addRow([
      scene.sceneCode || '',     // 场号
      formatInteriorExteriorForExport(language, scene.sceneInteriorExterior, secondaryLanguage),
      formatDayNightForExport(language, scene.sceneDayNight, secondaryLanguage),
      scene.sceneSet || '',
      scene.scenePlotDay || '',
      scene.scenePagesNumber || '',
      scene.scenePlot || '',
      scene.sceneLocation || '',  // 拍摄地点（不加粗）
      castTextBySceneId.get(scene.sceneID) || '',
      scene.scenePeriod || '',
      elements[0] || '', // 分组1
      elements[1] || '', // 分组2
      elements[3] || '', // 分组3（服裝/梳頭/化妝/特效化妝）
      elements[2] || '', // 分组4（美術道具）
      elements[4] || '', // 分组5
      elements[5] || '', // 分组6
      elements[6] || '', // 分组7
      elements[7] || ''  // 分组8
    ]);
    
    // 设置数据行样式（8号字体，不加粗，垂直居中，左对齐）
    row.eachCell((cell) => {
      cell.font = { size: 8, bold: false, color: { argb: hexToArgb(COLORS.BLACK) } };
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'left',
        wrapText: false // 关闭自动换行，允许内容溢出到右侧空单元格
      };
      // 设置边框颜色
      setCellBorder(cell);
    });
  });

  setsWorksheet.views = SHEET_FREEZE_VIEWS;
  applyScenesAndSetsPrintLayout(setsWorksheet, language);

  onProgress?.(80); // 数据准备完成，进入文件序列化阶段

  const tableTypeName = t('export.breakdownAndSetsTable', { lng: language });
  const fileName = buildExportDownloadFileName({
    lng: language,
    filmName,
    optionDisplayName: tableTypeName,
    optionFallbackI18nKey: 'export.breakdownAndSetsTable',
    breakdownDisplayName: breakdownInfo.breakdownName,
    extension: 'xlsx'
  });

  // 导出文件
  const buffer = await writeWorkbookBufferWithProgress(workbook, onProgress, { start: 80, end: 98 });
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  onProgress?.(100); // 已触发下载
};
